import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { useFormatter } from 'src/mtska-frontend-app-component/utils';
import { useResolvedMediaSrc } from 'src/mtska-frontend-data-provider/hooks/useResolvedMediaSrc';

const normalizeImageUrl = (url) => {
    if (!url) {
        return null;
    }
    if (typeof url === 'string') {
        return url;
    }
    if (typeof url === 'object' && typeof url.url === 'string') {
        return url.url;
    }
    return null;
};

const isPublicImageUrl = (url) => /^https?:\/\//i.test(url);

const GalleryPlaceholder = ({ className = '' }) => (
    <Box className={`property-gallery-image property-gallery-image-placeholder ${className}`.trim()} />
);

const GalleryImagePublic = ({ url, alt, className = '', eager = false }) => (
    <img
        className={`property-gallery-image ${className}`.trim()}
        src={url}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : 'auto'}
    />
);

const GalleryImageAuth = ({ url, alt, className = '', eager = false }) => {
    const { src, loading, ready } = useResolvedMediaSrc(url);

    if (!url) {
        return null;
    }

    if (loading || !ready) {
        return <GalleryPlaceholder className={className} />;
    }

    return (
        <img
            className={`property-gallery-image ${className}`.trim()}
            src={src}
            alt={alt}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={eager ? 'high' : 'auto'}
        />
    );
};

const GalleryImage = ({ url, alt, className = '', eager = false, enabled = true }) => {
    const normalizedUrl = normalizeImageUrl(url);

    if (!enabled || !normalizedUrl) {
        return <GalleryPlaceholder className={className} />;
    }

    if (isPublicImageUrl(normalizedUrl)) {
        return (
            <GalleryImagePublic
                url={normalizedUrl}
                alt={alt}
                className={className}
                eager={eager}
            />
        );
    }

    return (
        <GalleryImageAuth
            url={normalizedUrl}
            alt={alt}
            className={className}
            eager={eager}
        />
    );
};

const LazyGalleryThumb = ({ url, alt, isActive, onClick }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(isActive);

    useEffect(() => {
        if (isActive) {
            setIsVisible(true);
            return undefined;
        }

        const element = ref.current;
        if (!element || typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '80px' }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [isActive, url]);

    return (
        <button
            ref={ref}
            type="button"
            className={`property-gallery-thumb${isActive ? ' active' : ''}`}
            onClick={onClick}
        >
            <GalleryImage
                url={url}
                alt={alt}
                className="property-gallery-thumb-image"
                eager={false}
                enabled={isVisible}
            />
        </button>
    );
};

const InfoHeaderProperty = ({ view, item, title, subtitle }) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatter();
    const [activeIndex, setActiveIndex] = useState(0);

    const images = useMemo(() => {
        if (Array.isArray(item?.scrapedImageUrls) && item.scrapedImageUrls.length > 0) {
            return item.scrapedImageUrls.map((url, index) => ({
                url,
                position: index,
                isMain: index === 0,
            }));
        }

        if (Array.isArray(item?.gallery) && item.gallery.length > 0) {
            return item.gallery;
        }

        return [];
    }, [item?.scrapedImageUrls, item?.gallery]);

    useEffect(() => {
        setActiveIndex(0);
    }, [images.length, item?.id]);

    const activeImage = images[activeIndex] ?? images[0] ?? null;
    const headerTitle = title ?? [
        item?.price != null ? formatCurrency(item.price) : null,
        item?.product ? t(item.product) : null,
    ].filter(Boolean).join(' | ');

    const headerSubtitle = subtitle ?? [
        item?.address,
        item?.streetNum,
        item?.city,
    ].filter(Boolean).join(', ');

    return (
        <Box className={`info-header info-header-${view}`}>
            {images.length > 0 && (
                <Box className="property-gallery">
                    <Box className="property-gallery-main">
                        <GalleryImage
                            url={activeImage?.url}
                            alt={t('property')}
                            className="property-gallery-main-image"
                            eager
                            enabled
                        />
                    </Box>
                    {images.length > 1 && (
                        <Box className="property-gallery-thumbs">
                            {images.map((image, index) => (
                                <LazyGalleryThumb
                                    key={`${image.url}-${index}`}
                                    url={image.url}
                                    alt={`${t('property')} ${index + 1}`}
                                    isActive={index === activeIndex}
                                    onClick={() => setActiveIndex(index)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            )}
            <Box className="info-wrapper">
                <span className="title">{headerTitle}</span>
                {headerSubtitle && <span className="subtitle">{headerSubtitle}</span>}
            </Box>
        </Box>
    );
};

export default InfoHeaderProperty;
